// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Summary Card - compact heads-up display by default, expands on click to show details.
 */
import React, { useState } from 'react'
import { cn, Card, MarkdownViewer } from '@/components/ui'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { getRiskLevel, getRiskBadgeClasses, computeRiskCounts } from '../../utils/risk'

interface Props {
  title?: string
  headline?: string
  summary?: string
  score?: number | null
  findings?: Array<{ riskScore?: number | null }>
  countLabel?: string
  className?: string
}

/** Fix LLM output: literal \n → newlines, • → markdown list */
function fixMarkdown(text: string): string {
  return text
    .replace(/\\n/g, '\n')
    .replace(/^• /gm, '- ')
    .replace(/\n• /g, '\n- ')
}

export const SummaryCard: React.FC<Props> = ({
  headline,
  summary,
  score,
  findings = [],
  countLabel = 'finding',
  className,
}) => {
  const [expanded, setExpanded] = useState(false)
  const riskCounts = computeRiskCounts(findings)

  const level = getRiskLevel(score)
  const colorClasses = getRiskBadgeClasses(level)
  const displayScore = typeof score === 'number' ? score : '—'
  const fixedSummary = summary ? fixMarkdown(summary) : undefined

  return (
    <Card data-testid="cr-summary-card" className={cn('border-slate-200 shadow-none', className)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div data-testid="cr-risk-badge" className={cn(
          'flex items-center justify-center rounded-lg px-3 py-1.5 border',
          colorClasses
        )}>
          <span className="text-xl font-bold tabular-nums leading-none">
            {displayScore}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {headline || 'Contract Analysis'}
          </p>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-muted-foreground">{riskCounts.high}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-muted-foreground">{riskCounts.medium}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-muted-foreground">{riskCounts.low}</span>
          </span>
          <span className="text-muted-foreground/60 ml-1">
            {findings.length} {findings.length === 1 ? countLabel : `${countLabel}s`}
          </span>
        </div>

        {fixedSummary && (
          <div className="text-muted-foreground/50">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        )}
      </button>

      {expanded && fixedSummary && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100">
          <div className="text-sm leading-relaxed text-muted-foreground [&_strong]:text-foreground [&_strong]:font-semibold">
            <MarkdownViewer content={fixedSummary} />
          </div>
        </div>
      )}
    </Card>
  )
}
